import React, { ReactNode } from 'react';

import { Icon, PrimaryButton } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';

import { DeliveryStatus, IRestaurantNode } from 'generated/rbi-graphql';
import { useSelectRestaurant } from 'hooks/store-card';
import { ServiceMode } from 'state/order';
import { useStoreContext } from 'state/store';
import { UnavailabilityReason, getUnavailabilityReason } from 'utils/delivery';
import { fullBrandName } from 'utils/environment';
import { checkServiceModeUnavailable } from 'utils/restaurant';

import { DeliveryUnavailable } from '../delivery-unavailable';

import {
  ContentText,
  UnavailabilityReasonIconContainer,
  UnavailabilityReasonSubtitleContainer,
} from './styled';

interface DeliveryPausedContainerProps {
  unavailabilityReason: string | null | ReactNode;
  restaurant: IRestaurantNode;
  onClose: VoidFunction;
}

export const DeliveryPausedContainer = ({
  unavailabilityReason,
  restaurant,
  onClose,
}: DeliveryPausedContainerProps) => {
  const { store } = useStoreContext();
  const { formatMessage } = useIntl();
  const { selectRestaurant } = useSelectRestaurant({
    restaurant,
  });

  const {
    errorModalTitle = formatMessage({ id: 'deliveryNotAvailable' }),
    errorModalSubTitle,
    errorModalBody,
    errorModalFeedback,
    errorModalIcon,
  } = getUnavailabilityReason(
    unavailabilityReason as UnavailabilityReason,
    DeliveryStatus.QUOTE_UNAVAILABLE,
    formatMessage
  ) || {};

  const isPickupOpen = !checkServiceModeUnavailable(ServiceMode.TAKEOUT, store);

  const modalTitle = unavailabilityReason
    ? errorModalTitle
    : formatMessage({ id: 'yourLocationCannotAcceptDelivery' });

  const unavilableReasonMessage = (
    <>
      <UnavailabilityReasonSubtitleContainer>
        {errorModalSubTitle}
        {errorModalIcon && (
          <UnavailabilityReasonIconContainer>
            <Icon icon={errorModalIcon} color="icon-default" width="15px" aria-hidden />
          </UnavailabilityReasonIconContainer>
        )}
      </UnavailabilityReasonSubtitleContainer>
      <ContentText>{errorModalBody}</ContentText>
      <ContentText>{errorModalFeedback}</ContentText>
      <ContentText>
        {formatMessage({ id: 'unavailabilityReasonAcknowledgments' }, { brand: fullBrandName() })}
      </ContentText>
    </>
  );

  const modalMessage = unavailabilityReason
    ? unavilableReasonMessage
    : formatMessage({ id: 'deliveryRestaurantUnavailable' });

  const renderPrimaryButton = isPickupOpen ? (
    <PrimaryButton
      fullWidth
      aria-label={formatMessage({ id: 'orderPickupMessage' })}
      onClick={() => selectRestaurant()}
    >
      {formatMessage({ id: 'orderPickupMessage' })}
    </PrimaryButton>
  ) : undefined;

  return (
    <DeliveryUnavailable
      title={modalTitle}
      message={modalMessage}
      primaryButton={renderPrimaryButton}
      close={onClose}
    />
  );
};
